"use client"
import { createTheme } from "@mui/material/styles"
import { sharedColors } from "./colors"
import { mediaQueries, mediaQueriesValues } from "./mediaQueries"
import "./types"

// Remove manually defined breakpoints object and functions
// const breakpoints = { ... }

export const sharedTheme = createTheme({
  direction: "rtl",
  // Let MUI generate breakpoint functions from values
  breakpoints: {
    values: mediaQueriesValues,
  },
  typography: {
    fontFamily: "var(--assistant)",
    h1: {
      fontSize: "40px",
      lineHeight: "140%",
      color: sharedColors.black,
      [`${mediaQueries.desktopM}`]: {
        fontSize: "56px",
        lineHeight: "140%",
      },
    },
    h2: {
      fontSize: "32px",
      lineHeight: "140%",
      color: sharedColors.black,
      [`${mediaQueries.desktopM}`]: {
        fontSize: "40px",
        lineHeight: "140%",
      },
    },
    h3: {
      fontSize: "24px",
      lineHeight: "140%",
      color: sharedColors.black,
      [`${mediaQueries.desktopM}`]: {
        fontSize: "24px",
        lineHeight: "140%",
      },
    },
    h4: {
      fontSize: "20px",
      lineHeight: "140%",

      color: sharedColors.black,
      [`${mediaQueries.desktopM}`]: {
        fontSize: "20px",
        lineHeight: "140%",
      },
    },
    body1: {
      fontSize: "18px",
      lineHeight: "140%",
      color: sharedColors.black,
    },
    body2: {
      fontSize: "16px",
      lineHeight: "140%",
      color: sharedColors.black,
    },
    body3: {
      fontSize: "14px",
      lineHeight: "140%",
      color: sharedColors.black,
    },
  },
  palette: {
    primary: {
      main: sharedColors.black,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          padding: "8px 16px",
          minWidth: "130px",
          borderRadius: "8px",
        },
        sizeSmall: {
          fontSize: "16px !important",
          height: "38px !important",
          lineHeight: "140% !important",
          padding: "8px 16px !important",
          borderRadius: "8px !important",
        },
        sizeLarge: {
          padding: "12px 24px !important",
          fontSize: "18px !important",
          lineHeight: "140% !important",
          height: "49px",
        },
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: sharedColors.darkGreen,
            color: sharedColors.white,

            "&:hover,&:focus-visible": {
              boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.32)",
            },
            "&:active": {
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              color: sharedColors.white,
              border: `1px solid ${sharedColors.grey}`,
              backgroundColor: sharedColors.grey,
            },
            "&:disabled": {
              color: sharedColors.white,
              border: `1px solid ${sharedColors.grey}`,
              backgroundColor: sharedColors.grey,
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            border: `1px solid ${sharedColors.darkGreen}`,
            color: sharedColors.darkGreen,
            backgroundColor: "transparent",

            "&:hover,&:focus-visible": {
              backgroundColor: "#fff",
              boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.32)",
            },
            "&:active": {},
            "&.Mui-disabled": {
              color: sharedColors.white,
              border: `1px solid ${sharedColors.grey}`,
              backgroundColor: sharedColors.grey,
            },
            "&:disabled": {
              color: sharedColors.grey,
              border: `1px solid ${sharedColors.grey}`,
            },
          },
        },
        {
          props: { variant: "text" },
          style: {},
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0px 4px 12px 0px #00000029",
          borderRadius: "0 0 8px 8px !important",
          "& .MuiList-root": {
            paddingBottom: 0,
            minWidth: "216px",
          },
          "& .MuiButtonBase-root": {
            padding: 0,
            fontSize: "16px",
            lineHeight: "24px",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          alignItems: "flex-end",
          "&.MuiInput-root::before": {
            borderBottom: `1px solid ${sharedColors.black}`,
            opacity: 0.3,
          },

          "&.Mui-error::before": {
            borderBottom: `1px solid ${sharedColors.black}`,
            opacity: 1,
          },

          "&.MuiInput-root::after": {
            borderBottom: `1px solid ${sharedColors.black}`,
          },

          "&.Mui-error::after": {
            borderBottom: `1px solid ${sharedColors.black}`,
            opacity: 1,
          },
          "& svg": {
            width: "23px !important",
            alignSelf: "center",
          },
          // Target root when child has aria-expanded=true
          "&:has([aria-expanded='true'])": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
        input: {
          fontSize: "16px",
          lineHeight: "24px",
          "&:focus::placeholder": {
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "22px",
          },
          "&::placeholder": {
            fontSize: "16px !important",
            lineHeight: "24px",
            color: sharedColors.black,
            fontWeight: "400",
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "& input::placeholder": {
            color: `${sharedColors.grey} !important`,
            opacity: 1,
          },
          "& textarea::placeholder": {
            color: `${sharedColors.grey} !important`,
            opacity: 1,
          },
        },
        notchedOutline: {
          borderColor: `${sharedColors.grey}`,
          borderWidth: "1px !important",
        },
      },
    },
    MuiInputLabel: {
      // Target the InputLabel component
      styleOverrides: {
        root: {
          // Define styles for different states
          color: sharedColors.black, // Default label color
          top: "3px",
          "&.Mui-focused": {
            color: sharedColors.black, // Color when the input is focused
          },
          "&.Mui-error": {
            color: sharedColors.black, // Color when there's an error
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: sharedColors.lightGrey,
        },
        indicator: {
          display: "none",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          height: "44px",
          fontSize: "16px",
          lineHeight: "140%",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
          flex: 1,
          minHeight: "44px",
          color: sharedColors.black,
          minWidth: "105px",
          "&.Mui-selected": {
            backgroundColor: sharedColors.darkGreen,
            color: sharedColors.white,
            "& svg": {
              stroke: "white",
            },
          },
          "&:hover, &.Mui-selected:hover": {
            backgroundColor: sharedColors.darkGreen,
            color: sharedColors.white,
            "& svg": {
              stroke: "white",
            },
          },
          "& svg": {
            stroke: "black",
          },
          "&.Mui-focusVisible": {
            outline: "2px solid black",
            outlineOffset: "-2px",
            boxShadow: "inset 0 0 0 4px white",
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        thumb: {
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
      },
    },
  },
})

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true // removes the `xs` breakpoint
    sm: true
    md: true
    lg: true
    xl: true
  }
}

export type Theme = typeof sharedTheme
